import {useApolloClient} from '@apollo/client';
import {useVirtualizer} from '@tanstack/react-virtual';
import {
  usePayeesWithLogosQuery,
  useSetPayeeLogoMutation,
  useClearPayeeLogoMutation,
} from '@transactions/graphql';
import * as React from 'react';
import updateTransactionLogos from 'src/apolloCache/updateTransactionLogos';
import {useBreadcrumb} from 'src/Breadcrumbs';
import {Button} from 'src/Button';
import {TextInput} from 'src/inputs/TextInput';
import {cn} from 'src/lib/utils';
import {LogoImage} from 'src/LogoImage';
import {LogoSelect} from 'src/LogoSelect';

type Filter = 'all' | 'assigned' | 'unassigned';

/**
 * Starting guess for a row's height, refined by measurement once each row
 * mounts. Rows are not uniform: below `sm` the picker wraps onto its own line,
 * which roughly doubles the row.
 */
const ESTIMATED_ROW_HEIGHT = 60;

/** Rows rendered beyond the viewport, so scrolling does not reveal blanks. */
const OVERSCAN = 8;

const FILTERS: Array<{value: Filter; label: string}> = [
  {value: 'all', label: 'All'},
  {value: 'unassigned', label: 'Unassigned'},
  {value: 'assigned', label: 'Assigned'},
];

export const PayeeLogosView = () => {
  useBreadcrumb({
    label: 'Payee logos',
    fallback: [{label: 'Settings', href: '/settings'}],
  });

  const client = useApolloClient();
  // cache-and-network: this list is derived from transactions, and no
  // transaction mutation invalidates it. Left on the default cache-first, a
  // revisit would keep showing payees that have since been renamed away and
  // counts that no longer match, with no request issued.
  const {
    data,
    loading,
    error: queryError,
    refetch,
  } = usePayeesWithLogosQuery({fetchPolicy: 'cache-and-network'});
  const [setPayeeLogo] = useSetPayeeLogoMutation();
  const [clearPayeeLogo] = useClearPayeeLogoMutation();

  const [query, setQuery] = React.useState('');
  const [filter, setFilter] = React.useState<Filter>('all');
  /**
   * Payees whose assignment is currently in flight. A set rather than a single
   * value: changing a second row before the first finishes would otherwise
   * overwrite the first payee — re-enabling its picker mid-flight — and the
   * first response to land would clear the guard for both.
   */
  const [saving, setSaving] = React.useState<ReadonlySet<string>>(new Set());
  const [error, setError] = React.useState<string | null>(null);

  const rows = React.useMemo(() => data?.payeesWithLogos ?? [], [data]);

  /** Logo slug -> every payee using it, so sharing is visible on each row. */
  const payeesByLogo = React.useMemo(() => {
    const map = new Map<string, string[]>();
    for (const row of rows) {
      if (!row.logo) continue;
      map.set(row.logo, [...(map.get(row.logo) ?? []), row.payee]);
    }
    return map;
  }, [rows]);

  const filtered = React.useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (filter === 'assigned' && !row.logo) return false;
      if (filter === 'unassigned' && row.logo) return false;
      if (!needle) return true;
      return (
        row.payee.toLowerCase().includes(needle) ||
        (row.logo ?? '').toLowerCase().includes(needle)
      );
    });
  }, [rows, query, filter]);

  const assignedCount = rows.filter((row) => row.logo).length;

  /**
   * The list scrolls in its own container so only the rows on screen are in
   * the DOM. Every row mounts a live combobox, so rendering the whole list
   * cost about 0.4ms and 8 DOM nodes per row — a keystroke in the search box
   * above took over a second once a few thousand payees were on the page.
   * Windowing makes that cost independent of how many payees there are, which
   * is what lets the full list be reachable by scrolling instead of only by
   * searching.
   */
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: OVERSCAN,
    // Payee is the row's identity, so a measured height follows its row
    // through a filter change rather than staying with the slot.
    getItemKey: (index) => filtered[index]?.payee ?? index,
  });
  const virtualRows = virtualizer.getVirtualItems();

  const handleChange = async (payee: string, logo: string) => {
    // Also serves as a guard against a second submission for the same payee
    // while the first is in flight, which would race two creates against the
    // unique {user, payee} index.
    setSaving((pending) => new Set(pending).add(payee));
    setError(null);
    try {
      if (logo) {
        await setPayeeLogo({variables: {payee, logo}});
      } else {
        await clearPayeeLogo({variables: {payee}});
      }
      // Transaction.logo is derived from the payee server-side, so Apollo
      // cannot know this mutation invalidated it. Without this, navigating back
      // to a cache-first transaction list shows the old logo indefinitely.
      updateTransactionLogos(client.cache, payee, logo || null);
      await refetch();
    } catch (caught) {
      // The picker has already cleared its query by now, so without this the
      // change silently vanishes and the row looks unchanged for no stated
      // reason.
      setError(
        `Could not update ${payee}: ${
          caught instanceof Error ? caught.message : 'unknown error'
        }`,
      );
    } finally {
      setSaving((pending) => {
        const next = new Set(pending);
        next.delete(payee);
        return next;
      });
    }
  };

  // Only block on the very first load; cache-and-network keeps `loading` true
  // during background refreshes, which would otherwise flash the whole page.
  if (loading && !data) {
    return (
      <div className="flex items-center justify-center p-8">
        <span className="text-muted-foreground">Loading payees...</span>
      </div>
    );
  }

  // A failed first load is otherwise indistinguishable from having no payees:
  // data stays undefined and the page renders a confident "0 of 0".
  if (queryError && !data) {
    return (
      <div className="flex flex-col items-center gap-3 p-8">
        <p className="text-destructive" role="alert">
          Could not load payees: {queryError.message}
        </p>
        <Button variant="outline" onClick={() => void refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  // A failed *background* refresh still has cached rows to show, so it must not
  // replace the page — but it must not be silent either. This list is derived
  // from transactions and nothing invalidates it, so the refresh is the only
  // thing correcting payees renamed or deleted elsewhere; swallowing its
  // failure presents a stale list as current, which is the exact problem
  // cache-and-network was added to solve.
  const refreshError = queryError && data ? queryError : null;

  return (
    // overflow-hidden, not auto: the list below owns the scrolling now, which
    // both keeps the search box in reach at any depth and gives the
    // virtualizer a scroll element with a bounded height to window against.
    // MainPane puts routes in a flex column with overflow-hidden, so this
    // element must claim its height with flex-1 or the list collapses.
    <div className="flex flex-1 flex-col gap-4 overflow-hidden p-4">
      <div>
        <h2 className="text-lg font-semibold">Payee logos</h2>
        <p className="text-muted-foreground text-sm">
          Assign a brand logo to a payee. Every transaction with that payee
          shows it — nothing is stored on the transactions themselves, so
          changing a logo here updates all of them at once.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-48 flex-1">
          <TextInput
            value={query}
            onChange={setQuery}
            placeholder="Search payees or logos…"
          />
        </div>
        <div className="flex gap-1">
          {FILTERS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setFilter(option.value)}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm',
                filter === option.value
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-accent/50',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <p className="text-muted-foreground text-sm">
        {assignedCount} of {rows.length} payees have a logo
        {filtered.length !== rows.length && <> · {filtered.length} shown</>}
      </p>

      {refreshError && (
        <div
          role="alert"
          className="border-destructive/40 bg-destructive/10 text-destructive flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm"
        >
          <span>
            Showing previously loaded payees — refreshing failed:{' '}
            {refreshError.message}
          </span>
          <Button variant="outline" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="border-destructive/40 bg-destructive/10 text-destructive rounded-lg border px-3 py-2 text-sm"
        >
          {error}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="border-border rounded-lg border border-dashed p-8 text-center">
          <p className="text-muted-foreground">No payees match</p>
        </div>
      ) : (
        <div
          ref={scrollRef}
          // min-h-0 with the default flex-shrink, not flex-1: the box must
          // size to its rows when there are few (flex-1 would stretch an
          // empty bordered panel down the page) and shrink to the space left
          // when there are many, which is when it starts scrolling. min-h-0
          // is what lets a flex child shrink below its content height at all.
          className="border-border min-h-0 overflow-y-auto rounded-lg border"
        >
          {/* Full-height spacer, so the scrollbar reflects every payee even
              though only the windowed rows exist. Rows are positioned inside
              it rather than laid out in flow. */}
          <div
            className="relative w-full"
            style={{height: `${virtualizer.getTotalSize()}px`}}
          >
            {virtualRows.map((virtualRow) => {
              const row = filtered[virtualRow.index];
              const sharing = (payeesByLogo.get(row.logo ?? '') ?? []).filter(
                (payee) => payee !== row.payee,
              );
              return (
                <div
                  key={virtualRow.key}
                  // measureElement reads data-index to record this row's real
                  // height, which the estimate above only approximates.
                  data-index={virtualRow.index}
                  ref={virtualizer.measureElement}
                  className={cn(
                    'absolute top-0 left-0 flex w-full flex-wrap items-center gap-3 p-3',
                    // Absolute rows are out of flow, so `divide-y` on the
                    // parent has nothing to divide; each row draws its own
                    // separator instead.
                    virtualRow.index > 0 && 'border-border border-t',
                  )}
                  style={{transform: `translateY(${virtualRow.start}px)`}}
                >
                  <LogoImage logo={row.logo} className="size-8" />
                  <div className="min-w-40 flex-1">
                    <div className="truncate font-medium">{row.payee}</div>
                    <div className="text-muted-foreground text-xs">
                      {row.transactionCount === 1
                        ? '1 transaction'
                        : `${row.transactionCount} transactions`}
                      {sharing.length > 0 && (
                        <>
                          {' · '}
                          <span title={sharing.join(', ')}>
                            shares {row.logo} with{' '}
                            {sharing.length === 1
                              ? sharing[0]
                              : `${sharing.length} other payees`}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="w-full sm:w-72">
                    <LogoSelect
                      label={null}
                      ariaLabel={`Logo for ${row.payee}`}
                      disabled={saving.has(row.payee)}
                      value={row.logo ?? ''}
                      onChange={(logo) => {
                        void handleChange(row.payee, logo);
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
